import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { environmentConfig } from "../environment";
import {
  Product,
  ProductsResponse,
  aeDeltaPerformanceReportResponse,
  aePerformanceReportResponse,
  branchAndLoanOfficerPerformanceReportResponse,
  tsDataResultsResponse,
  tsDataObject,
  GallusApiBaseResponse,
} from "./types/gallus";
import { getUserSessionItem } from "../";
import type {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
} from "@reduxjs/toolkit/query";
import { FetchBaseQueryArgs } from "@reduxjs/toolkit/dist/query/fetchBaseQuery";
import Bugsnag from "@bugsnag/js";
import { CognitoOidcConfig } from "../CognitoOidcConfig";

/* function isFetchArgs(args: string | FetchArgs): args is FetchArgs {
  return typeof args !== "string";
} */

const gallusApiBaseURL: FetchBaseQueryArgs = {
  baseUrl: environmentConfig.backendApiEndpoint,
  prepareHeaders: (headers) => {
    //headers.set("SAToken", getUserSessionItem("SAToken") ?? "");
    return headers;
  },
};

const baseQuery = fetchBaseQuery(gallusApiBaseURL);
const baseQueryWithErrorHandling: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  let result = await baseQuery(args, api, extraOptions);

  if (result.error) {
    Bugsnag.notify(new Error("Gallus API Error"), function (event) {
      event.severity = "error";
      event.setUser(
        getUserSessionItem("userName") ?? "User not in UserSession",
      );
      event.addMetadata("API Arguments", {
        endpoint: api.endpoint,
        args: JSON.stringify(args),
      });
    });
  }
  return result;
};

export const gallusApi = createApi({
  reducerPath: "gallusApi",
  baseQuery: baseQueryWithErrorHandling,
  endpoints: (builder) => ({
    getProducts: builder.query<Product[], void>({
      query: () => {
        let urlSearchParams = new URLSearchParams();
        urlSearchParams.set("service", "Product_View");
        let bodyFormData = new URLSearchParams();
        bodyFormData.set("idToken", getUserSessionItem("idToken")!);
        bodyFormData.set("accessToken", getUserSessionItem("accessToken")!);
        bodyFormData.set("clientId", CognitoOidcConfig.clientId);
        return {
          url: `/services/?${urlSearchParams.toString()}`,
          body: bodyFormData,
          method: "post",
        };
      },
      transformResponse: (response: ProductsResponse) => {
        switch (response.retCode) {
          case 200:
            return response.results as Product[];
          default:
            throw new Error(JSON.stringify(response));
        }
      },
    }),
    userCheckIn: builder.query<GallusApiBaseResponse, void>({
      query: () => {
        let urlSearchParams = new URLSearchParams();
        urlSearchParams.set("service", "New_User_Checkin");
        let bodyFormData = new URLSearchParams();
        bodyFormData.set("idToken", getUserSessionItem("idToken")!);
        bodyFormData.set("accessToken", getUserSessionItem("accessToken")!);
        bodyFormData.set("clientId", CognitoOidcConfig.clientId);
        return {
          url: `/services/?${urlSearchParams.toString()}`,
          method: "post",
          body: bodyFormData,
        };
      },
    }),
    TSUserCreation: builder.query<GallusApiBaseResponse, {
      username: string,
      email: string,
      orgName: string
    }>({
      query: (arg) => {
        let urlSearchParams = new URLSearchParams();
        urlSearchParams.set("service", "TS_UserCreation_V3");
        let bodyFormData = new URLSearchParams();
        bodyFormData.set("idToken", getUserSessionItem("idToken")!);
        bodyFormData.set("accessToken", getUserSessionItem("accessToken")!);
        bodyFormData.set("clientId", CognitoOidcConfig.clientId);
        bodyFormData.set("CognitoUsername", arg.username);
        bodyFormData.set("Email", arg.email);
        bodyFormData.set("OrgName", arg.orgName);
        return {
          url: `/services/?${urlSearchParams.toString()}`,
          method: "post",
          body: bodyFormData,
        };
      },
    }),
    CHMAccountExecutives_ViewOrderedByMetricsMonthAndYear: builder.query<
      aePerformanceReportResponse,
      {
        year: string;
        month: string;
        timePeriod: "QTD" | "YTD" | "ALL" | undefined;
      }
    >({
      query: (arg) => {
        let urlSearchParams = new URLSearchParams();
        urlSearchParams.set("service", "CHMAEsPerformanceReport_View");
        urlSearchParams.set("Year", arg.year);
        urlSearchParams.set("Month", arg.month);
        if (typeof arg.timePeriod !== "undefined") {
          urlSearchParams.set("TimePeriod", arg.timePeriod);
        }
        let bodyFormData = new URLSearchParams();
        bodyFormData.set("idToken", getUserSessionItem("idToken")!);
        bodyFormData.set("accessToken", getUserSessionItem("accessToken")!);
        bodyFormData.set("clientId", CognitoOidcConfig.clientId);
        return {
          url: `/services/?${urlSearchParams.toString()}`,
          body: bodyFormData,
          method: "post",
        };
      },
      transformResponse: (response: aePerformanceReportResponse) => {
        switch (response.retCode) {
          case 200:
            return response;
          default:
            throw new Error(JSON.stringify(response));
        }
      },
    }),
    CHMBranches_ViewOrderedByMetricsMonthAndYear: builder.query<
      branchAndLoanOfficerPerformanceReportResponse,
      {
        year: string;
        month: string;
        timePeriod: "QTD" | "YTD" | "ALL" | undefined;
      }
    >({
      query: (arg) => {
        let urlSearchParams = new URLSearchParams();
        urlSearchParams.set("service", "CHMBranches_ViewOrderedByMetrics");
        urlSearchParams.set("Year", arg.year);
        urlSearchParams.set("Month", arg.month);
        if (typeof arg.timePeriod !== "undefined") {
          urlSearchParams.set("TimePeriod", arg.timePeriod);
        }
        let bodyFormData = new URLSearchParams();
        bodyFormData.set("idToken", getUserSessionItem("idToken")!);
        bodyFormData.set("accessToken", getUserSessionItem("accessToken")!);
        bodyFormData.set("clientId", CognitoOidcConfig.clientId);
        return {
          url: `/services/?${urlSearchParams.toString()}`,
          body: bodyFormData,
          method: "post",
        };
      },
      transformResponse: (
        response: branchAndLoanOfficerPerformanceReportResponse,
      ) => {
        switch (response.retCode) {
          case 200:
            return response;
          default:
            throw new Error(JSON.stringify(response));
        }
      },
    }),
    CHMLoanOfficers_ViewOrderedByMetricsMonthAndYear: builder.query<
      branchAndLoanOfficerPerformanceReportResponse,
      {
        year: string;
        month: string;
        timePeriod: "QTD" | "YTD" | "ALL" | undefined;
      }
    >({
      query: (arg) => {
        let urlSearchParams = new URLSearchParams();
        urlSearchParams.set("service", "CHMLOs_ViewOrderedByMetrics");
        urlSearchParams.set("Year", arg.year);
        urlSearchParams.set("Month", arg.month);
        if (typeof arg.timePeriod !== "undefined") {
          urlSearchParams.set("TimePeriod", arg.timePeriod);
        }
        let bodyFormData = new URLSearchParams();
        bodyFormData.set("idToken", getUserSessionItem("idToken")!);
        bodyFormData.set("accessToken", getUserSessionItem("accessToken")!);
        bodyFormData.set("clientId", CognitoOidcConfig.clientId);
        return {
          url: `/services/?${urlSearchParams.toString()}`,
          body: bodyFormData,
          method: "post",
        };
      },
      transformResponse: (
        response: branchAndLoanOfficerPerformanceReportResponse,
      ) => {
        switch (response.retCode) {
          case 200:
            return response;
          default:
            throw new Error(JSON.stringify(response));
        }
      },
    }),
    Demo_ViewOrderedByMetricsMonthAndYear: builder.query<
      aePerformanceReportResponse,
      {
        year: string;
        month: string;
        timePeriod: "QTD" | "YTD" | "ALL" | undefined;
      }
    >({
      query: (arg) => {
        let urlSearchParams = new URLSearchParams();
        urlSearchParams.set(
          "service",
          "DemoAEPerformance2_ViewOrderedByMetrics",
        );
        urlSearchParams.set("Year", arg.year);
        urlSearchParams.set("Month", arg.month);
        if (typeof arg.timePeriod !== "undefined") {
          urlSearchParams.set("TimePeriod", arg.timePeriod);
        }
        let bodyFormData = new URLSearchParams();
        bodyFormData.set("idToken", getUserSessionItem("idToken")!);
        bodyFormData.set("accessToken", getUserSessionItem("accessToken")!);
        bodyFormData.set("clientId", CognitoOidcConfig.clientId);
        return {
          url: `/services/?${urlSearchParams.toString()}`,
          body: bodyFormData,
          method: "post",
        };
      },
      transformResponse: (response: aePerformanceReportResponse) => {
        switch (response.retCode) {
          case 200:
            return response;
          default:
            throw new Error(JSON.stringify(response));
        }
      },
    }),
    TS_CHMAEsPerfSummary2YTD: builder.query<tsDataObject, void>({
      query: () => {
        let urlSearchParams = new URLSearchParams();
        urlSearchParams.set("service", "TS_CHMAEsPerfSummary2YTD");
        let bodyFormData = new URLSearchParams();
        bodyFormData.set("idToken", getUserSessionItem("idToken")!);
        bodyFormData.set("accessToken", getUserSessionItem("accessToken")!);
        bodyFormData.set("clientId", CognitoOidcConfig.clientId);
        return {
          url: `/services/?${urlSearchParams.toString()}`,
          body: bodyFormData,
          method: "post", //removed sectoken in body
        };
      },
      transformResponse: (response: any) => {
        switch (response.retCode) {
          case 200:
            return response;
          default:
            throw new Error(JSON.stringify(response));
        }
      },
    }),
    TS_Delta_View: builder.query<
      aeDeltaPerformanceReportResponse,
      { period: "MTD" | "QTD" | "YTD" }
    >({
      query: (arg) => {
        let urlSearchParams = new URLSearchParams();
        switch (arg.period) {
          case "MTD":
            urlSearchParams.set("service", "TS_DeltaMTD_View");
            break;
          case "QTD":
            urlSearchParams.set("service", "TS_DeltaQTD_View");
            break;
          case "YTD":
            urlSearchParams.set("service", "TS_DeltaYTD_View");
            break;
        }

        let bodyFormData = new URLSearchParams();
        bodyFormData.set("idToken", getUserSessionItem("idToken")!);
        bodyFormData.set("accessToken", getUserSessionItem("accessToken")!);
        bodyFormData.set("clientId", CognitoOidcConfig.clientId);
        return {
          url: `/services/?${urlSearchParams.toString()}`,
          body: bodyFormData,
          method: "post",
        };
      },
      transformResponse: (response: any) => {
        switch (response.retCode) {
          case 200:
            return response;
          default:
            throw new Error(JSON.stringify(response));
        }
      },
    }),
    TrialBalance_GetFiscalYearFiscalQuarter: builder.query<any, void>({
      query: () => {
        let urlSearchParams = new URLSearchParams();
        urlSearchParams.set(
          "service",
          "TrialBalance_GetFiscalYearFiscalQuarter",
        );
        let bodyFormData = new URLSearchParams();
        bodyFormData.set("idToken", getUserSessionItem("idToken")!);
        bodyFormData.set("accessToken", getUserSessionItem("accessToken")!);
        bodyFormData.set("clientId", CognitoOidcConfig.clientId);
        return {
          url: `/services/?${urlSearchParams.toString()}`,
          body: bodyFormData,
          method: "post",
        };
      },
      transformResponse: (response: any) => {
        switch (response.retCode) {
          case 200:
            return response;
          default:
            throw new Error(JSON.stringify(response));
        }
      },
    }),
    TrialBalance_Get: builder.query<any, void>({
      query: () => {
        let urlSearchParams = new URLSearchParams();
        urlSearchParams.set("service", "TrialBalance_Get");
        let bodyFormData = new URLSearchParams();
        bodyFormData.set("idToken", getUserSessionItem("idToken")!);
        bodyFormData.set("accessToken", getUserSessionItem("accessToken")!);
        bodyFormData.set("clientId", CognitoOidcConfig.clientId);
        return {
          url: `/services/?${urlSearchParams.toString()}`,
          body: bodyFormData,
          method: "post",
        };
      },
      transformResponse: (response: any) => {
        switch (response.retCode) {
          case 200:
            return response;
          default:
            throw new Error(JSON.stringify(response));
        }
      },
    }),
    TS_CHMWholesaleQTD: builder.query<tsDataObject, void>({
      query: () => {
        let urlSearchParams = new URLSearchParams();
        urlSearchParams.set("service", "TS_CHMAEsPerfSummary2QTD");
        let bodyFormData = new URLSearchParams();
        bodyFormData.set("idToken", getUserSessionItem("idToken")!);
        bodyFormData.set("accessToken", getUserSessionItem("accessToken")!);
        bodyFormData.set("clientId", CognitoOidcConfig.clientId);
        return {
          url: `/services/?${urlSearchParams.toString()}`,
          body: bodyFormData,
          method: "post",
        };
      },
      transformResponse: (response: tsDataResultsResponse) => {
        switch (response.retCode) {
          case 200:
            return response.results;
          default:
            throw new Error(JSON.stringify(response));
        }
      },
    }),
    TS_CHMWholesaleMTD: builder.query<
      tsDataObject,
      { year: string; month: string }
    >({
      query: (arg) => {
        let urlSearchParams = new URLSearchParams();
        urlSearchParams.set("service", "TS_CHMAEsPerfSummary2MTD");
        urlSearchParams.set("Year", arg.year);
        urlSearchParams.set("Month", arg.month);
        let bodyFormData = new URLSearchParams();
        bodyFormData.set("idToken", getUserSessionItem("idToken")!);
        bodyFormData.set("accessToken", getUserSessionItem("accessToken")!);
        bodyFormData.set("clientId", CognitoOidcConfig.clientId);
        return {
          url: `/services/?${urlSearchParams.toString()}`,
          body: bodyFormData,
          method: "post",
        };
      },
      transformResponse: (response: tsDataResultsResponse) => {
        switch (response.retCode) {
          case 200:
            return response.results;
          default:
            throw new Error(JSON.stringify(response));
        }
      },
    }),
    TS_RetailDashboard: builder.query<
      tsDataObject,
      {
        timePeriod: "MTD" | "QTD" | "YTD";
        date?: { year: string; month: string };
      }
    >({
      query: (arg) => {
        let urlSearchParams = new URLSearchParams();
        urlSearchParams.set("service", "TS_RetailDashboard_View");
        urlSearchParams.set("TimePeriod", arg.timePeriod);
        if (typeof arg.date !== "undefined") {
          urlSearchParams.set("Year", arg.date.year);
          urlSearchParams.set("Month", arg.date.month);
        }
        let bodyFormData = new URLSearchParams();
        bodyFormData.set("idToken", getUserSessionItem("idToken")!);
        bodyFormData.set("accessToken", getUserSessionItem("accessToken")!);
        bodyFormData.set("clientId", CognitoOidcConfig.clientId);
        return {
          url: `/services/?${urlSearchParams.toString()}`,
          body: bodyFormData,
          method: "post",
        };
      },
      transformResponse: (response: tsDataResultsResponse) => {
        switch (response.retCode) {
          case 200:
            return response.results;
          default:
            throw new Error(JSON.stringify(response));
        }
      },
    }),
    BalanceSheet_Get: builder.query<any, void>({
      query: () => {
        let urlSearchParams = new URLSearchParams();
        urlSearchParams.set("service", "BalanceSheet_Get");
        let bodyFormData = new URLSearchParams();
        bodyFormData.set("idToken", getUserSessionItem("idToken")!);
        bodyFormData.set("accessToken", getUserSessionItem("accessToken")!);
        bodyFormData.set("clientId", CognitoOidcConfig.clientId);
        return {
          url: `/services/?${urlSearchParams.toString()}`,
          body: bodyFormData,
          method: "post",
        };
      },
      transformResponse: (response: any) => {
        switch (response.retCode) {
          case 200:
            return response;
          default:
            throw new Error(JSON.stringify(response));
        }
      },
    }),
    LiveboardExternalSiteCheck: builder.query<
      number | undefined,
      { liveboardGUID: string }
    >({
      query: (arg) => {
        let urlSearchParams = new URLSearchParams();
        urlSearchParams.set("service", "TS_CheckLiveboardInExternalSite");
        urlSearchParams.set("LiveboardGUID", arg.liveboardGUID);
        return {
          url: `/services/?${urlSearchParams.toString()}`,
          method: "get", //removed sectoken in body
        };
      },
      transformResponse: (response: any) => {
        switch (response.retCode) {
          case 200:
            return Number(response.results);
          case 400:
            return undefined;
          default:
            throw new Error(JSON.stringify(response));
        }
      },
    }),
  }),
});

export const {
  useGetProductsQuery,
  useLazyGetProductsQuery,
  useLazyUserCheckInQuery,
  useLazyCHMAccountExecutives_ViewOrderedByMetricsMonthAndYearQuery,
  useLazyCHMBranches_ViewOrderedByMetricsMonthAndYearQuery,
  useLazyCHMLoanOfficers_ViewOrderedByMetricsMonthAndYearQuery,
  useLazyDemo_ViewOrderedByMetricsMonthAndYearQuery,
  useLazyTS_Delta_ViewQuery,
  useLazyTS_CHMAEsPerfSummary2YTDQuery,
  useTrialBalance_GetFiscalYearFiscalQuarterQuery,
  useTrialBalance_GetQuery,
  useLazyTS_CHMWholesaleQTDQuery,
  useLazyTS_CHMWholesaleMTDQuery,
  useLazyTS_RetailDashboardQuery,
  useBalanceSheet_GetQuery,
  useLiveboardExternalSiteCheckQuery,
  useLazyTSUserCreationQuery
} = gallusApi;
